import type { Locale } from '../i18n/locale';
import { START, isStartId } from '../content/start';

/**
 * Intake draft model: validation and plain-text summary per locale. No transport, no persistence of
 * contact data. Categories and scopes are stored as ids so a draft survives a language switch.
 */
export const CATEGORY_IDS = ['hospitality', 'energy', 'construction', 'manufacturing', 'other'] as const;
export const SCOPE_IDS = ['unica', 'proyecto', 'recurrente'] as const;

const LABELS = {
  'es-CO': {
    categories: { hospitality: 'Hotelería y FF&E', energy: 'Energía y proyectos eléctricos', construction: 'Construcción y acabados', manufacturing: 'Manufactura y operación', other: 'Otra' },
    scopes: { unica: 'Compra única', proyecto: 'Proyecto', recurrente: 'Compra recurrente' },
    steps: ['Necesidad', 'Contexto', 'Empresa', 'Resumen'],
    errors: {
      need: 'Describa la necesidad con un poco más de detalle (mínimo 12 caracteres).', category: 'Seleccione la categoría más cercana.', city: 'Indique la ciudad de entrega en Colombia.',
      company: 'Indique el nombre de la empresa.', name: 'Indique su nombre.', email: 'Ingrese un correo corporativo válido.', consent: 'Necesitamos su autorización para contactarle sobre esta solicitud.',
    },
    summary: { title: 'COLESCOMEX SUPPLY — Solicitud de proyecto', need: 'Necesidad', category: 'Categoría', scope: 'Alcance', quantity: 'Cantidad aproximada', reference: 'Referencia o proveedor actual', delivery: 'Entrega', by: 'requerida para', company: 'Empresa', contact: 'Contacto', email: 'Correo', phone: 'Teléfono' },
  },
  en: {
    categories: { hospitality: 'Hospitality & FF&E', energy: 'Energy & electrical projects', construction: 'Construction & finishes', manufacturing: 'Manufacturing & operations', other: 'Other' },
    scopes: { unica: 'One-time purchase', proyecto: 'Project', recurrente: 'Recurring purchase' },
    steps: ['Need', 'Context', 'Company', 'Summary'],
    errors: {
      need: 'Please describe the need in a little more detail (at least 12 characters).', category: 'Select the closest category.', city: 'Enter the delivery city in Colombia.',
      company: 'Enter the company name.', name: 'Enter your name.', email: 'Enter a valid business email address.', consent: 'We need your authorization to contact you about this request.',
    },
    summary: { title: 'COLESCOMEX SUPPLY — Project request', need: 'Need', category: 'Category', scope: 'Scope', quantity: 'Approximate quantity', reference: 'Reference or current supplier', delivery: 'Delivery', by: 'required by', company: 'Company', contact: 'Contact', email: 'Email', phone: 'Phone' },
  },
} as const satisfies Record<Locale, unknown>;

export const categories = (locale: Locale) => CATEGORY_IDS.map(id => [id, LABELS[locale].categories[id]] as const);
export const scopes = (locale: Locale) => SCOPE_IDS.map(id => [id, LABELS[locale].scopes[id]] as const);
export const steps = (locale: Locale) => LABELS[locale].steps;
export const categoryLabel = (id: string, locale: Locale) => (CATEGORY_IDS as readonly string[]).includes(id) ? LABELS[locale].categories[id as (typeof CATEGORY_IDS)[number]] : '';
export const scopeLabel = (id: string, locale: Locale) => (SCOPE_IDS as readonly string[]).includes(id) ? LABELS[locale].scopes[id as (typeof SCOPE_IDS)[number]] : '';
/** Spanish defaults kept for existing callers. */
export const STEPS = LABELS['es-CO'].steps;

export interface Draft { start: string; need: string; category: string; scope: string; quantity: string; reference: string; city: string; date: string; company: string; name: string; role: string; email: string; phone: string; consent: boolean }
export const EMPTY: Draft = { start: '', need: '', category: '', scope: '', quantity: '', reference: '', city: '', date: '', company: '', name: '', role: '', email: '', phone: '', consent: false };
export type Errors = Partial<Record<keyof Draft, string>>;

export function validate(step: number, draft: Draft, locale: Locale = 'es-CO'): Errors {
  const messages = LABELS[locale].errors; const errors: Errors = {};
  if (step === 0 && draft.need.trim().length < 12) errors.need = messages.need;
  if (step === 1) {
    if (!draft.category) errors.category = messages.category;
    if (!draft.city.trim()) errors.city = messages.city;
  }
  if (step === 2) {
    if (!draft.company.trim()) errors.company = messages.company;
    if (!draft.name.trim()) errors.name = messages.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(draft.email.trim())) errors.email = messages.email;
    if (!draft.consent) errors.consent = messages.consent;
  }
  return errors;
}

export function summaryText(draft: Draft, locale: Locale = 'es-CO') {
  const t = LABELS[locale].summary;
  return [
    t.title, '',
    ...(isStartId(draft.start) ? [`${START[locale].summary}: ${START[locale].options[draft.start].title}`] : []),
    `${t.need}: ${draft.need.trim()}`, `${t.category}: ${categoryLabel(draft.category, locale) || draft.category || '—'}`, `${t.scope}: ${scopeLabel(draft.scope, locale) || '—'}`,
    `${t.quantity}: ${draft.quantity.trim() || '—'}`, `${t.reference}: ${draft.reference.trim() || '—'}`,
    `${t.delivery}: ${draft.city.trim() || '—'}${draft.date ? ` · ${t.by} ${draft.date}` : ''}`, '',
    `${t.company}: ${draft.company.trim()}`, `${t.contact}: ${draft.name.trim()}${draft.role.trim() ? ` · ${draft.role.trim()}` : ''}`,
    `${t.email}: ${draft.email.trim()}`, `${t.phone}: ${draft.phone.trim() || '—'}`,
  ].join('\n');
}
